/**
 * 移动端全屏文件预览弹窗（图片 + PDF）。
 *
 * 用法：
 *   const [previewFile, setPreviewFile] = useState<{ url: string; name: string; type?: 'image' | 'pdf' } | null>(null);
 *   ...
 *   <ImagePreview
 *     open={!!previewFile}
 *     url={previewFile?.url}
 *     name={previewFile?.name}
 *     type={previewFile?.type}
 *     onClose={() => setPreviewFile(null)}
 *   />
 */
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PdfPreview from './PdfPreview';

interface ImagePreviewProps {
  open: boolean;
  url?: string;
  name?: string;
  /** 文件类型：image 用 img 渲染，pdf 用 pdf.js 渲染为 Canvas */
  type?: 'image' | 'pdf';
  onClose: () => void;
}

export default function ImagePreview({ open, url, name, type = 'image', onClose }: ImagePreviewProps) {
  if (!url) return null;

  const isPdf = type === 'pdf';
  const title = name || (isPdf ? 'PDF 预览' : '图片预览');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.92)',
          boxShadow: 'none',
          m: 0,
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            color: '#fff',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              maxWidth: '70%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              component="a"
              href={url}
              download={name || 'file'}
              sx={{ color: '#fff' }}
              size="small"
            >
              <DownloadIcon />
            </IconButton>
            <IconButton onClick={onClose} sx={{ color: '#fff' }} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1,
            overflow: 'auto',
          }}
          onClick={onClose}
        >
          {isPdf ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                overflow: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <PdfPreview url={url!} name={name} bgColor="#1e1e1e" />
            </Box>
          ) : (
            <img
              src={url}
              alt={title}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
